import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Star, Facebook, ExternalLink } from "lucide-react";

export default function AdminPartnerConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newFb, setNewFb] = useState("");

  useEffect(() => { api.get("/admin/partner-config").then((r)=>setCfg(r.data)); }, []);
  if (!cfg) return <div className="text-slate-500">Chargement…</div>;

  const setField = (k, v) => setCfg((s)=>({ ...s, [k]: v }));
  const setFounders = (k, v) => setCfg((s)=>({ ...s, founders: { ...(s.founders||{}), [k]: v } }));
  const setPlan = (idx, k, v) => setCfg((s)=> {
    const plans = [...(s.plans||[])];
    plans[idx] = { ...plans[idx], [k]: v };
    return { ...s, plans };
  });
  const setPlanFeatures = (idx, text) => setPlan(idx, "features", text.split("\n").map((l)=>l.trim()).filter(Boolean));
  const move = (idx, dir) => setCfg((s)=> {
    const plans = [...(s.plans||[])];
    const j = idx + dir;
    if (j < 0 || j >= plans.length) return s;
    [plans[idx], plans[j]] = [plans[j], plans[idx]];
    plans.forEach((p, i) => { p.order = i + 1; });
    return { ...s, plans };
  });
  const addPlan = () => setCfg((s)=> ({ ...s, plans: [ ...(s.plans||[]),
    { key: `plan-${Date.now()}`, name: "Nouvelle formule", order: (s.plans||[]).length + 1, active: true,
      price_ht: 0, price_display: "0 €", period: "/ mois",
      badge: "", highlight: false, tagline: "", features: [], cta_label: "Choisir", trial_note: "", facebook_note: "" } ]}));
  const delPlan = (idx) => {
    const p = cfg.plans[idx];
    if (!confirm(`⚠️ Supprimer la formule « ${p?.name || p?.key} » ?\nElle disparaîtra immédiatement de la page publique après sauvegarde.`)) return;
    setCfg((s)=>({ ...s, plans: (s.plans||[]).filter((_,i)=>i!==idx) }));
  };

  // ----- Facebook groups -----
  const fbGroups = cfg.facebook_groups || [];
  const addFb = () => {
    const url = (newFb || "").trim();
    try {
      const u = new URL(url);
      if (!u.hostname.includes("facebook.com")) throw new Error("not fb");
    } catch {
      toast.error("URL Facebook invalide (attendu : https://www.facebook.com/groups/…)");
      return;
    }
    if (fbGroups.includes(url)) { toast.error("Ce groupe est déjà dans la liste"); return; }
    setCfg((s)=>({ ...s, facebook_groups: [...(s.facebook_groups||[]), url] }));
    setNewFb("");
  };
  const delFb = (i) => {
    const url = fbGroups[i];
    if (!confirm(`Retirer ce groupe Facebook ?\n${url}`)) return;
    setCfg((s)=>({ ...s, facebook_groups: (s.facebook_groups||[]).filter((_,j)=>j!==i) }));
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/admin/partner-config", {
        trial_days: cfg.trial_days,
        founders: cfg.founders,
        plans: cfg.plans,
        facebook_groups: cfg.facebook_groups,
      });
      toast.success("Configuration enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="admin-partner-config-title">Configuration « Devenir partenaire »</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Tarifs, avantages, période d'essai et offre fondateurs.</p>
        </div>
        <Button disabled={loading} onClick={save} className="btn-shine text-white" data-testid="partner-config-save">
          <Save size={14} className="mr-1"/> {loading ? "…" : "Enregistrer"}
        </Button>
      </div>

      {/* Global */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <div className="font-heading font-bold text-lg">Paramètres généraux</div>
          <div>
            <Label>Jours d'essai gratuit</Label>
            <Input type="number" min="0" value={cfg.trial_days ?? 30}
                   onChange={(e)=>setField("trial_days", parseInt(e.target.value||"0",10))}
                   data-testid="partner-trial-days"/>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <div className="font-heading font-bold text-lg flex items-center gap-2"><Star size={16} className="text-[#E05638]"/> Offre fondateurs</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!cfg.founders?.active}
                   onChange={(e)=>setFounders("active", e.target.checked)}
                   className="w-4 h-4" data-testid="founders-active"/>
            Afficher cette section sur la page publique
          </label>
          <div>
            <Label>Texte de l'offre</Label>
            <Textarea rows={3} value={cfg.founders?.text || ""} onChange={(e)=>setFounders("text", e.target.value)} data-testid="founders-text"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Partenaires fondateurs restants</Label>
              <Input type="number" min="0" value={cfg.founders?.remaining_count ?? 0}
                     onChange={(e)=>setFounders("remaining_count", parseInt(e.target.value||"0",10))}
                     data-testid="founders-remaining"/>
              <p className="text-[10px] text-slate-500 mt-1">Le compteur décrémente automatiquement à chaque inscription.</p>
            </div>
            <div>
              <Label>Remise préférentielle (%)</Label>
              <Input type="number" min="0" max="100" value={cfg.founders?.discount_percent ?? 0}
                     onChange={(e)=>setFounders("discount_percent", parseInt(e.target.value||"0",10))}
                     data-testid="founders-discount"/>
            </div>
          </div>
        </div>
      </div>

      {/* Formules */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-bold">Formules</h2>
          <Button variant="outline" onClick={addPlan} data-testid="add-plan-btn"><Plus size={14} className="mr-1"/> Ajouter une formule</Button>
        </div>
        <div className="space-y-3">
          {(cfg.plans || []).map((p, idx) => (
            <div key={p.key + idx} className={`bg-white border rounded-2xl p-5 ${p.highlight ? "border-[#E05638] ring-1 ring-[#E05638]/20" : "border-slate-200"}`}
                 data-testid={`plan-editor-${p.key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-mono">#{idx+1}</span>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={p.active !== false}
                           onChange={(e)=>setPlan(idx, "active", e.target.checked)}
                           className="w-3.5 h-3.5"/> Active
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={!!p.highlight}
                           onChange={(e)=>setPlan(idx, "highlight", e.target.checked)}
                           className="w-3.5 h-3.5"/> Mise en avant
                  </label>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={()=>move(idx, -1)} className="p-1 rounded hover:bg-slate-100" title="Monter"><ArrowUp size={14}/></button>
                  <button onClick={()=>move(idx, 1)} className="p-1 rounded hover:bg-slate-100" title="Descendre"><ArrowDown size={14}/></button>
                  <button onClick={()=>delPlan(idx)} className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Supprimer"><Trash2 size={14}/></button>
                </div>
              </div>

              <div className="mt-3 grid md:grid-cols-3 gap-3">
                <div><Label>Clé</Label><Input value={p.key} onChange={(e)=>setPlan(idx, "key", e.target.value.toLowerCase().replace(/\s+/g,'-'))}/></div>
                <div><Label>Nom</Label><Input value={p.name} onChange={(e)=>setPlan(idx, "name", e.target.value)}/></div>
                <div><Label>Badge (optionnel)</Label><Input value={p.badge || ""} onChange={(e)=>setPlan(idx, "badge", e.target.value)}/></div>
                <div><Label>Prix (€/mois)</Label><Input type="number" step="0.01" value={p.price_ht ?? 0} onChange={(e)=>setPlan(idx, "price_ht", parseFloat(e.target.value||"0"))}/></div>
                <div><Label>Prix affiché</Label><Input value={p.price_display || ""} onChange={(e)=>setPlan(idx, "price_display", e.target.value)}/></div>
                <div><Label>Période</Label><Input value={p.period || ""} onChange={(e)=>setPlan(idx, "period", e.target.value)} placeholder="/ mois"/></div>
                <div className="md:col-span-3"><Label>Sous-titre</Label><Input value={p.tagline || ""} onChange={(e)=>setPlan(idx, "tagline", e.target.value)}/></div>
                <div className="md:col-span-2"><Label>Mention essai (ex : 30 jours offerts)</Label><Input value={p.trial_note || ""} onChange={(e)=>setPlan(idx, "trial_note", e.target.value)}/></div>
                <div><Label>Libellé bouton</Label><Input value={p.cta_label || ""} onChange={(e)=>setPlan(idx, "cta_label", e.target.value)}/></div>
                <div className="md:col-span-3">
                  <Label>Avantages (une ligne = un avantage)</Label>
                  <Textarea rows={6} value={(p.features || []).join("\n")} onChange={(e)=>setPlanFeatures(idx, e.target.value)}/>
                </div>
                <div className="md:col-span-3">
                  <Label>Note Facebook / campagnes (optionnel)</Label>
                  <Input value={p.facebook_note || ""} onChange={(e)=>setPlan(idx, "facebook_note", e.target.value)}
                         placeholder="Ex : Pas de campagne publicitaire Facebook incluse."/>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Groupes Facebook */}
      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Facebook size={18} className="text-blue-600"/>
          <h2 className="font-heading text-xl font-bold">Réseau de communautés Facebook</h2>
          <span className="ml-auto text-xs text-slate-500">{fbGroups.length} groupe(s)</span>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Ces groupes ne sont pas exposés publiquement (seul le nombre est affiché sur la page). Ils servent de référence interne pour vos campagnes de mise en avant.
        </p>
        <div className="flex gap-2 mb-3">
          <Input value={newFb} onChange={(e)=>setNewFb(e.target.value)}
                 placeholder="https://www.facebook.com/groups/…"
                 onKeyDown={(e)=>{ if(e.key==="Enter"){ e.preventDefault(); addFb(); } }}
                 data-testid="fb-new-input"/>
          <Button variant="outline" onClick={addFb} data-testid="fb-add-btn"><Plus size={14}/> Ajouter</Button>
        </div>
        <ul className="space-y-1">
          {fbGroups.map((url, i) => (
            <li key={url} className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded-lg" data-testid={`fb-row-${i}`}>
              <span className="text-slate-400 text-xs font-mono">#{i+1}</span>
              <a href={url} target="_blank" rel="noreferrer" className="flex-1 truncate text-slate-700 hover:text-[#E05638] inline-flex items-center gap-1">
                <ExternalLink size={12}/> {url}
              </a>
              <button onClick={()=>delFb(i)} className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Retirer" data-testid={`fb-del-${i}`}>
                <Trash2 size={14}/>
              </button>
            </li>
          ))}
          {fbGroups.length === 0 && <li className="text-xs text-slate-500 text-center py-4">Aucun groupe pour l'instant.</li>}
        </ul>
      </div>

      <div className="mt-6 flex justify-end">
        <Button disabled={loading} onClick={save} className="btn-shine text-white" data-testid="partner-config-save-bottom">
          <Save size={14} className="mr-1"/> {loading ? "…" : "Enregistrer la configuration"}
        </Button>
      </div>
    </div>
  );
}
